import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { copyToClipboard, parseApiError } from '@saas-kit/lib';
import { Button, ConfirmDialog, toast } from '@saas-kit/ui';

import { resetTenantOwnerPassword, type TenantOwnerPasswordReset } from './api';

interface Props {
  tenantId: string;
  disabled?: boolean;
}

/**
 * Reset the tenant owner's login password. The backend generates the new
 * password; we reveal it once so the platform admin can hand it over.
 */
export function TenantResetPasswordAction({
  tenantId,
  disabled = false,
}: Props): JSX.Element {
  const { t } = useTranslation('adminTenants');
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<TenantOwnerPasswordReset | null>(null);

  const mutation = useMutation({
    mutationFn: () => resetTenantOwnerPassword(tenantId),
    onSuccess: (data) => {
      setConfirming(false);
      setResult(data);
    },
    onError: (err) => toast.error(parseApiError(err).message),
  });

  async function copyPassword(): Promise<void> {
    if (!result) return;
    const copied = await copyToClipboard(result.password);
    if (copied) {
      toast.success(t('reset_password.copied'));
    } else {
      toast.error(t('reset_password.copy_failed'));
    }
  }

  return (
    <>
      <Button
        variant="secondary"
        disabled={disabled || mutation.isPending}
        onClick={() => setConfirming(true)}
      >
        {t('actions.reset_password')}
      </Button>

      {confirming ? (
        <ConfirmDialog
          title={t('confirm.reset_password.title')}
          body={t('confirm.reset_password.body')}
          confirmLabel={t('actions.reset_password')}
          variant="destructive"
          isBusy={mutation.isPending}
          onCancel={() => setConfirming(false)}
          onConfirm={() => mutation.mutate()}
        />
      ) : null}

      {result ? (
        <ConfirmDialog
          title={t('reset_password.dialog_title')}
          confirmLabel={t('reset_password.copy')}
          cancelLabel={t('reset_password.done')}
          onConfirm={() => void copyPassword()}
          onCancel={() => setResult(null)}
        >
          <div className="mt-2 space-y-3">
            <p className="text-sm text-muted-foreground">
              {t('reset_password.intro')}
            </p>
            <dl className="space-y-3 rounded-md border border-border bg-muted/40 p-3">
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t('reset_password.owner_label')}
                </dt>
                <dd className="font-mono text-sm">{result.email}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t('reset_password.password_label')}
                </dt>
                <dd className="select-all break-all font-mono text-base font-semibold text-foreground">
                  {result.password}
                </dd>
              </div>
            </dl>
          </div>
        </ConfirmDialog>
      ) : null}
    </>
  );
}
